import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { trpc } from "../_core/trpc";
import HeartScene from "../components/three/HeartScene";
import { useReducedMotion } from "../components/three/scene-utils";
import { Countdown } from "../components/lux/Countdown";
import {
  Emojiful,
  FilmGrade,
  IntroVeil,
  LightRays,
  PetalField,
  Rule,
  useHeroScroll
} from "../components/lux/Atmosphere";
import { formatInZone, zoneLabel } from "@/shared/birthday";
import { MusicToggle } from "../components/lux/MusicToggle";
import { toTracks } from "../components/lux/music-engine";
import Experience from "./Experience";
import { MidnightFireworks } from "../components/lux/MidnightFireworks";
function Home() {
  const reduced = useReducedMotion();
  const state = trpc.public.state.useQuery(void 0, {
    // Poll while locked so a visitor who leaves the tab open overnight sees the
    // reveal without touching anything. The local 1s tick drives the digits;
    // this poll is only the authority on locked-vs-unlocked.
    refetchInterval: (query) => {
      const d = query.state.data;
      return d && d.unlocked ? false : 6e4;
    },
    staleTime: 3e4
  });
  const utils = trpc.useUtils();
  const [revealing, setRevealing] = useState(false);
  const revealTimer = useRef(void 0);
  const [introDone, setIntroDone] = useState(reduced);
  const [celebrating, setCelebrating] = useState(() => new URLSearchParams(window.location.search).has("celebrate"));
  const endCelebration = useCallback(() => setCelebrating(false), []);
  const handleIntroDone = useCallback(() => setIntroDone(true), []);
  const onArrive = useMemo(
    () => () => {
      setRevealing(true);
      if (!reduced) setCelebrating(true);
      void utils.public.state.invalidate();
      revealTimer.current = window.setTimeout(() => setRevealing(false), 2600);
    },
    [utils, reduced]
  );
  useEffect(() => () => window.clearTimeout(revealTimer.current), []);
  const fireworks = celebrating && <MidnightFireworks onDone={endCelebration} />;
  const title = state.data?.siteTitle ?? "Midnight for Baby";
  if (state.isLoading) {
    return <>
        <IntroVeil siteTitle={title} reduced={reduced} onDone={handleIntroDone} />
        <main className="grid min-h-[100svh] place-items-center bg-[#0b0c16]">
          <div className="text-center">
            <p className="eyebrow anim-fade">Midnight for Baby</p>
            <div className="mx-auto mt-6 h-px w-24 overflow-hidden bg-white/10">
              <div
      className="h-px w-10"
      style={{
        background: "linear-gradient(90deg,transparent,#e9a8f2,transparent)",
        animation: "lux-shimmer 1.6s linear infinite"
      }}
    />
            </div>
          </div>
        </main>
      </>;
  }
  if (state.error) {
    return <main className="grid min-h-[100svh] place-items-center px-5">
        <div className="glass max-w-md rounded-2xl p-8 text-center">
          <h1 className="display-md">Couldn&apos;t load this just now</h1>
          <p className="mt-3 text-sm leading-relaxed text-[color:var(--muted-foreground)]">
            The site couldn&apos;t reach its own server. Please check your connection and try again.
          </p>
          <button type="button" className="btn-gold mt-6" onClick={() => void state.refetch()}>
            Try again
          </button>
        </div>
      </main>;
  }
  const data = state.data;
  if (data.unlocked) {
    return <>
        <IntroVeil siteTitle={data.siteTitle} reduced={reduced} onDone={handleIntroDone} />
        <Experience data={data} introDone={introDone} />
        {fireworks}
      </>;
  }
  return <>
      <IntroVeil siteTitle={data.siteTitle} reduced={reduced} onDone={handleIntroDone} />
      {fireworks}
      <Teaser
    data={data}
    reduced={reduced}
    revealing={revealing}
    onArrive={onArrive}
    introDone={introDone}
  />
    </>;
}
function Teaser({
  data,
  reduced,
  revealing,
  onArrive,
  introDone
}) {
  const birthday = new Date(data.birthdayAt);
  const scrollRef = useHeroScroll();
  const tracks = useMemo(() => toTracks(data.teaserAudio), [data.teaserAudio]);
  return <main className="no-hscroll relative min-h-[100svh] overflow-hidden">
      <MusicToggle tracks={tracks} />
      <FilmGrade reduced={reduced} />

      {
    /* ── 3D stage (behind everything, non-interactive) ── */
  }
      <div className="pointer-events-none fixed inset-0 z-0">
        <LightRays intensity={2} reduced={reduced} />
        <HeartScene
    intensity={2}
    mode="teaser"
    className="absolute inset-0"
    scrollRef={scrollRef}
  />
        <PetalField count={6} className="z-0" />
      </div>

      {
    /* ── the midnight flash, only while the countdown arrives ── */
  }
      <div
    aria-hidden="true"
    className="pointer-events-none fixed inset-0 z-40"
    style={{
      opacity: revealing ? 1 : 0,
      transition: revealing ? "opacity .9s ease" : "opacity 1.6s ease",
      background: "radial-gradient(circle at 50% 45%, rgba(235,225,255,.92), rgba(244,166,198,.5) 30%, rgba(91,33,182,.35) 62%, transparent 82%)"
    }}
  />

      <div
    className="relative z-10 mx-auto flex min-h-[100svh] w-full max-w-3xl flex-col items-center justify-center px-5 text-center sm:px-8"
    style={{
      // Everything in the teaser rises from the intro veil rather than
      // appearing behind it mid-animation.
      paddingTop: "calc(4rem + env(safe-area-inset-top, 0px))",
      paddingBottom: "calc(4rem + env(safe-area-inset-bottom, 0px))",
      opacity: introDone ? 1 : 0,
      transition: "opacity 1.2s ease .15s"
    }}
  >
        <p className="eyebrow anim-fade">A private little world</p>

        <h1 className="display-lg gold-text anim-fade-up delay-1 mt-5 max-w-2xl">
          <Emojiful text={data.teaserText} />
        </h1>

        <Rule className="anim-fade delay-2 my-8 w-32" />

        <p className="prose-romance anim-fade-up delay-2 max-w-xl text-balance">
          The doors open at the very first minute of your birthday. Until then: a garden of light, a
          countdown, and me, thinking of you. 💫
        </p>

        <div className="anim-fade-up delay-3 mt-10 w-full">
          <Countdown
    targetIso={data.birthdayAt}
    serverNowIso={data.now}
    onArrive={onArrive}
    reduced={reduced}
  />
        </div>

        <p className="anim-fade delay-4 mt-5 px-2 text-[0.72rem] uppercase leading-relaxed tracking-[0.18em] text-[#c3c7de]">
          {formatInZone(birthday, data.timezone)} · {zoneLabel(birthday, data.timezone)}
        </p>

        {data.teaserMessages.length > 0 && <div className="mt-12 w-full max-w-lg space-y-3">
            {data.teaserMessages.map((m) => <div key={m.id} className="glass-soft anim-fade-up rounded-xl p-5 text-left">
                <p className="eyebrow">{m.title}</p>
                <p className="prose-romance mt-2 whitespace-pre-line">{m.body}</p>
              </div>)}
          </div>}

        <p className="anim-fade delay-5 mt-14 max-w-sm text-[0.82rem] leading-relaxed tracking-wide text-[#b9bdd6]">
          Patience, my love. Some things are worth the wait.
        </p>

        {
    /* Scroll affordance: a hairline that falls, so the page reads as the
       beginning of something rather than a single centred card. */
  }
        <div
    aria-hidden="true"
    className="anim-fade delay-5 mt-10 flex flex-col items-center gap-2"
    style={{ opacity: introDone ? 1 : 0, transition: "opacity 1.4s ease 1s" }}
  >
          <span className="text-[0.55rem] uppercase tracking-[0.3em] text-[color:var(--gold-dim)] opacity-60">
            scroll
          </span>
          <span className="relative block h-10 w-px overflow-hidden bg-white/10">
            <span
    className="absolute inset-x-0 top-0 block h-4 bg-[color:var(--gold)]"
    style={{ animation: reduced ? void 0 : "cue-fall 2.4s ease-in-out infinite" }}
  />
          </span>
        </div>
      </div>

      <style>{`
        @keyframes cue-fall {
          0%   { transform: translateY(-100%); opacity: 0; }
          35%  { opacity: 1; }
          100% { transform: translateY(280%); opacity: 0; }
        }
        /* The DOM petals are a light dusting over the whole frame. Because they are
           scattered randomly, one will occasionally land on the countdown or the
           headline \u2014 so they are pushed BEHIND the reading layer (z -1) rather than
           above it. The 3D petals in the WebGL scene still drift in front, where
           they are small and genuinely out of focus. */
        .teaser-petals { z-index: -1; }
        @media (prefers-reduced-motion: reduce) {
          [style*="cue-fall"] { animation: none !important; }
        }
      `}</style>
    </main>;
}
export {
  Home as default
};
